import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import Cookies from 'js-cookie'
import axios, { setCurrentUserTimezone } from '@/lib/axios'
import { getErrorMessage } from '@/lib/utils'
import { SuccessResponseType } from '@/types/general'

type LogoutStateType = {
  isLoading: boolean
  isLoggedOut: boolean
  error: {
    status?: number
    message: string
  } | null
}

type LogoutStoreType = {
  logoutState: LogoutStateType
  logout: () => Promise<SuccessResponseType>
  forceLogout: () => void
  reset: () => void
}

const initialState: LogoutStateType = {
  isLoading: false,
  isLoggedOut: false,
  error: null
}

const useLogoutStore = create<LogoutStoreType>()(
  devtools(set => ({
    logoutState: initialState,
    logout: async () => {
      set(() => ({
        logoutState: {
          ...initialState,
          isLoading: true
        }
      }))

      try {
        const { data } = await axios.get<SuccessResponseType>('/auth/logout')

        // Clear cookies
        Cookies.remove('accessToken')
        Cookies.remove('refreshToken')
        Cookies.remove('rememberMe')

        // Clear timezone
        setCurrentUserTimezone(null)

        set(() => ({
          logoutState: {
            ...initialState,
            isLoggedOut: true
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          logoutState: {
            isLoading: false,
            isLoggedOut: false,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))

        throw error
      }
    },
    reset: () => {
      set(() => ({
        logoutState: initialState
      }))
    },
    forceLogout: () => {
      Cookies.remove('accessToken')
      Cookies.remove('refreshToken')
      Cookies.remove('rememberMe')
      setCurrentUserTimezone(null)
      set(() => ({
        logoutState: {
          ...initialState,
          isLoggedOut: true
        }
      }))
    }
  }))
)

export default useLogoutStore
